import { FC, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Grid, TextField, Typography } from '@mui/material';
import { MINUTE_IN_SECONDS, SECOND_IN_MS } from 'constants/times';
import { Field, FieldProps, Form, Formik } from 'formik';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { BackButton } from 'components/_basic/BackButton/BackButton';
import { Button } from 'components/_basic/Button/Button';
import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { FormField } from 'components/_basic/Form/FormField';

import ToastService from 'helpers/ToastService';
import { confirmationCodeSchema } from 'helpers/validations/signFormsValidation';

import { IConfirmationCodeProps as IProps } from './types';

import styles from './ConfirmationCodeForm.module.scss';

export const ConfirmationCodeForm: FC<IProps> = ({
  emailValue,
  isPending,
  codeValue = '',
  codeError = '',
  formText = '',
  backClickHandler,
  closeClickHandler,
  resendCodeHandler,
  onSubmit,
}): ReactElement => {
  const { t } = useTranslation();
  const [isResendButtonActive, setIsResendButtonActive] = useState(true);
  const [delay, setDelay] = useState(MINUTE_IN_SECONDS);
  const text = formText || t('codeConfirmationMessage', { email: emailValue });
  const { isAuthenticated } = useAuthenticated();

  const updateCount = (num: number): void => {
    setIsResendButtonActive(false);
    let leftNum = num;
    const timer = setInterval(() => {
      leftNum = --leftNum;
      setDelay(leftNum);
    }, SECOND_IN_MS);
    setTimeout((): void => {
      clearInterval(timer);
      setIsResendButtonActive(true);
      setDelay(num);
    }, num * SECOND_IN_MS);
  };

  useEffect(() => {
    if (codeError.length === 0) {
      updateCount(MINUTE_IN_SECONDS);
    }
  }, [codeError]);

  const resendCode = (): Promise<void> =>
    resendCodeHandler({ username: emailValue })
      .then(() => updateCount(delay))

      .catch((error) => {
        if (error instanceof Error) {
          ToastService.showError(error.message);
        }

        if (process.env.NODE_ENV === 'development') {
          console.error(error);
        }
      });

  return (
    <Box className={styles.confirmationCodeWrapper}>
      <Grid container alignItems="center">
        <Grid item xs={11}>
          <BackButton onClick={backClickHandler} />
        </Grid>
        {isAuthenticated && (
          <Grid item xs={1} className={styles.closeButton}>
            <CloseButton onClick={closeClickHandler} />
          </Grid>
        )}
      </Grid>
      <Typography variant="h1" className={styles.formTitle}>
        {t('checkYourEmail')}
      </Typography>
      <Typography variant="body2">{text}</Typography>
      <Formik initialValues={{ code: codeValue }} onSubmit={onSubmit} validationSchema={confirmationCodeSchema}>
        {({ isValid }) => (
          <Form>
            <Field name="code">
              {({ field, meta }: FieldProps<string>) => (
                <FormField
                  id={'code'}
                  label={t('code')}
                  error={(meta.touched && meta.error) || codeError}
                  sx={{ marginBottom: '24px' }}
                >
                  <TextField {...field} inputProps={{ maxLength: 6 }} />
                </FormField>
              )}
            </Field>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              isLoading={isPending}
              disabled={Boolean(!isValid) || isPending}
            >
              {t('continue')}
            </Button>
          </Form>
        )}
      </Formik>
      {isResendButtonActive ? (
        <Button fullWidth variant="text" onClick={resendCode}>
          {t('resend')}
        </Button>
      ) : (
        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          {t('resendNotificationMessage', { seconds: delay })}
        </Typography>
      )}
    </Box>
  );
};
